import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogListing = () => {
  const { categorySlug } = useParams();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [blogsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/blogs/published`, {
            params: selectedCategory ? { category_slug: selectedCategory } : {}
          }),
          axios.get(`${API}/categories`)
        ]);
        setBlogs(blogsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  useEffect(() => {
    setSelectedCategory(categorySlug || null);
  }, [categorySlug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    let decoded = textarea.value;
    
    if (decoded.includes('&lt;') || decoded.includes('&gt;')) {
      textarea.innerHTML = decoded;
      decoded = textarea.value;
    }
    
    const tmp = document.createElement('DIV');
    tmp.innerHTML = decoded;
    return tmp.textContent || tmp.innerText || '';
  };

  const getExcerpt = (blog) => {
    if (blog.excerpt) return blog.excerpt;
    const text = stripHtml(blog.content);
    return text.length > 120 ? text.substring(0, 120) + '...' : text;
  };

  return (
    <PublicLayout>
      {/* Hero Section - Compact */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-12 md:py-16" data-testid="blog-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="font-['Manrope'] text-3xl md:text-4xl font-bold text-[#1a1a1a] tracking-tight mb-4">
              Blog & Articles
            </h1>
            <p className="text-slate-600 text-base md:text-lg">
              Expert advice on nutrition, diet planning, and healthy living tips.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      {categories.length > 0 && (
        <section className="py-6 border-b border-slate-100 bg-white" data-testid="category-filter">
          <div className="container mx-auto px-4 md:px-8 max-w-6xl">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedCategory 
                    ? 'bg-[#4A7BF7] text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category.slug 
                      ? 'bg-[#4A7BF7] text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${category.slug}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="py-12 md:py-16 bg-white" data-testid="blog-grid">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden border border-slate-200">
                  <div className="skeleton h-48 w-full"></div>
                  <div className="p-5">
                    <div className="skeleton h-3 w-20 mb-3 rounded"></div>
                    <div className="skeleton h-5 w-full mb-2 rounded"></div>
                    <div className="skeleton h-4 w-3/4 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                <Tag className="w-6 h-6 text-slate-400" />
              </div>
              <h3 className="font-['Manrope'] text-lg font-semibold text-slate-900 mb-2">
                No posts yet
              </h3>
              <p className="text-slate-500 text-sm">
                {selectedCategory 
                  ? 'No posts found in this category.'
                  : 'Check back soon for new content!'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, index) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 flex flex-col"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`blog-card-${blog.slug}`}
                >
                  {/* Featured Image */}
                  <div className="relative h-48 overflow-hidden bg-slate-100">
                    {blog.featured_image ? (
                      <img
                        src={blog.featured_image.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${blog.featured_image}` 
                          : blog.featured_image}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                        <span className="text-4xl font-bold text-slate-300">{blog.title.charAt(0)}</span>
                      </div>
                    )}
                    {/* Category Badge */}
                    {blog.category_name && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-slate-700 rounded-full">
                        {blog.category_name}
                      </span>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-5 flex flex-col flex-1">
                    {/* Date */}
                    <span className="text-slate-400 text-xs flex items-center gap-1 mb-2">
                      <Calendar className="w-3 h-3" />
                      {formatDate(blog.published_at)}
                    </span>

                    {/* Title */}
                    <h2 className="font-['Manrope'] font-semibold text-[#1a1a1a] text-lg mb-2 group-hover:text-[#4A7BF7] transition-colors line-clamp-2">
                      {blog.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-slate-500 text-sm leading-relaxed flex-1 mb-4 line-clamp-3">
                      {getExcerpt(blog)}
                    </p>

                    {/* Read More */}
                    <div className="flex items-center text-[#4A7BF7] font-medium text-sm group-hover:gap-2 gap-1 transition-all">
                      Read More
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* App Download CTA */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">

            {/* Left: Logo + Text */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex justify-center md:justify-start mb-5">
                <img src="/logo.png" alt="WingsDiet" className="h-12 w-auto" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-3 leading-tight">
                Eat Smart,<br className="hidden md:block" /> Live Better
              </h2>
              <p className="text-slate-500 text-base mb-7 max-w-sm mx-auto md:mx-0">
                Track meals, get personalized diet plans, and hit your health goals — all in one place. Free to download.
              </p>

              {/* Store Badges */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <a
                  href="https://play.google.com/store/apps/details?id=com.wingsdiet.nutrition"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 bg-[#1a1a1a] text-white px-5 py-3 rounded-xl hover:bg-[#333] transition-colors group"
                >
                  <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
                    <path d="M7.1 4.4L27.7 24 7.1 43.6a3 3 0 01-1.1-2.3V6.7a3 3 0 011.1-2.3z" fill="#EA4335"/>
                    <path d="M34.7 17.3L28 20.9 7.1 4.4a3 3 0 011.7-.4l25.9 13.3z" fill="#FBBC04"/>
                    <path d="M34.7 30.7L8.8 44a3 3 0 01-1.7-.4L27.7 24l7 6.7z" fill="#34A853"/>
                    <path d="M44 24c0 1.3-.7 2.5-1.9 3.1l-7.4 3.6L28 24l6.7-6.7 7.4 3.6A3.5 3.5 0 0144 24z" fill="#4285F4"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-white/60 leading-none uppercase tracking-wide">Get it on</div>
                    <div className="text-base font-semibold leading-tight">Google Play</div>
                  </div>
                </a>

                <a
                  href="https://apps.apple.com/us/app/wingsdiet/id6748774685"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 bg-[#1a1a1a] text-white px-5 py-3 rounded-xl hover:bg-[#333] transition-colors group"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-white/60 leading-none uppercase tracking-wide">Download on the</div>
                    <div className="text-base font-semibold leading-tight">App Store</div>
                  </div>
                </a>
              </div>

              {/* Rating */}
              <div className="mt-6 flex items-center gap-2 justify-center md:justify-start">
                <div className="flex text-yellow-400 text-sm">★★★★★</div>
                <span className="text-slate-500 text-sm">4.8 &nbsp;·&nbsp; 204 Reviews</span>
              </div>
            </div>

            {/* Right: Feature pills */}
            <div className="flex-1 w-full max-w-sm mx-auto md:mx-0">
              <div className="bg-slate-50 rounded-3xl p-7 space-y-4">
                {[
                  { icon: '🥗', title: 'Smart Meal Tracking', desc: 'Log meals instantly with AI-powered food recognition' },
                  { icon: '📊', title: 'Nutrition Analytics', desc: 'Real-time macros: carbs, protein, fat & calories' },
                  { icon: '🎯', title: 'Personalized Plans', desc: 'Diet plans tailored to your body and goals' },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-4 bg-white rounded-2xl p-4 shadow-sm">
                    <span className="text-2xl">{f.icon}</span>
                    <div>
                      <div className="font-semibold text-[#1a2332] text-sm">{f.title}</div>
                      <div className="text-slate-500 text-xs mt-0.5 leading-relaxed">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default BlogListing;
